import React, { memo, useCallback, useEffect, useRef, useState } from "react";
import styled from "styled-components";
import { useNavigate, useSearchParams } from "react-router-dom";
import useOnclickOutside from "react-cool-onclickoutside";
import { useTranslation } from "react-i18next";
import {
  FontSettingsIcon,
  SearchIcon,
} from "../../../../../common/assets/icons";
import ThreeDotsIcon from "../../../../../common/assets/icons/three_dots_icon";
import { useAppContext } from "../../../../../app_context";
import CheckIcon from "../../../../../common/assets/icons/admin_page_icons/check_icon";
import FontSettingsBlock from "../../../bible_page_desktop/bible_navigation_panel_desktop/bible_search_and_settings/font_settings_block";
import BibleHistoryIcon from "../../../../../common/assets/icons/bible_page_icons/bible_history_icon";
import BibleHistoryItem from "../../../bible_page_desktop/bible_navigation_panel_desktop/bible_search_and_settings/bible_history_block/bible_history_item";
import { useBiblePageContext } from "../../../bible_page_context";

const RightButtonsContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  height: 32px;
`;

const BibleSearchButton = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 38px;
  background: #fff;
  border-radius: 15px;
  box-shadow: 0px 0px 6px 0px rgba(0, 0, 0, 0.12);
  height: 32px;
`;

const BibleFontSettingsButton = styled(BibleSearchButton)`
  height: 32px;
`;

const ThreeDotsIconStyled = styled(ThreeDotsIcon)`
  height: 32px;
  gap: 3px;
`;

const SettingsBlockWrapper = styled.div`
  position: absolute;
  top: 52px;
  right: 12px;
  width: max-content;
`;

const SettingsBlockButtonsContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  padding: 6px 8px;
  background: #fff;
  border-radius: 15px;
  box-shadow: 4.462px 4.462px 10px 0px rgba(0, 0, 0, 0.12);
`;

const SettingsBlockButton = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 11px;
  min-height: 32px;
  max-height: 36px;
  max-width: 220px;
  padding: 5px 8px;
  color: #1e1e1e;
  font-size: 14px;
  font-weight: 400;
  border-radius: 8px;
  user-select: none;

  &:active {
    background: linear-gradient(
      180deg,
      rgba(81, 114, 234, 0.25) -18.38%,
      rgba(122, 90, 238, 0.25) 108.82%
    );
  }
`;

const CheckIconStyled = styled(CheckIcon)`
  width: 12px;
  height: 12px;
  margin-bottom: 2px;
`;

const StrongButton = styled.div`
  display: flex;
  align-items: center;
  gap: 5px;
  color: #1e1e1e;
  font-size: 12px;
  background: #fff;
  padding: 0 13px;
  margin-right: 12px;
  height: 24px;
  border-radius: 15px;
  user-select: none;
  cursor: pointer;
  transition: 0.2s;
  box-shadow: rgba(0, 0, 0, 0.1) 0px 1px 2px 0px;

  svg {
    margin-bottom: 0px;
  }
`;

const FontSettingsIconStyled = styled(FontSettingsIcon)`
  height: 22px;
  width: 22px;
`;

const BibleHistoryIconStyled = styled(BibleHistoryIcon)`
  height: 16px;
  width: 16px;
`;

const BibleHistoryList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 7px;
  flex: 1;
  width: 100%;
  max-height: calc(100vh - 300px);
  overflow-y: auto;
  padding: 5px 4px 5px 0px;

  ::-webkit-scrollbar {
    width: 2px;
  }
`;

const BibleMainRightButtons = ({
  bibleId,
  isFirstTranslation,
  activeScreenMobile,
  showStrongsCode,
  bibleTranslations,
  setActiveScreenMobile,
  setOpenBibleSearch,
  setShowStrongsCode,
}) => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const settingsRef = useRef(null);
  const settingsFontRef = useRef(null);
  const settingsButtonRef = useRef(null);
  const historyRef = useRef(null);
  const historyButtonRef = useRef(null);

  const { t } = useTranslation();

  const { bibleFontSettings, setBibleFontSettings } = useAppContext();
  const { bibleHistory } = useBiblePageContext();

  const [showSettingsBlock, setShowSettingsBlock] = useState(false);
  const [showFontSettings, setShowFontSettings] = useState(false);
  const [showBibleHistory, setShowBibleHistory] = useState(false);

  const hasStrongsBible = bibleId === "rst";

  useOnclickOutside(() => setShowSettingsBlock(false), {
    refs: [settingsRef, settingsButtonRef],
  });
  useOnclickOutside(() => setShowFontSettings(false), {
    refs: [settingsFontRef, settingsButtonRef],
  });
  useOnclickOutside(() => setShowBibleHistory(false), {
    refs: [historyRef, historyButtonRef],
  });

  useEffect(() => {
    setShowSettingsBlock(false);
    setShowFontSettings(false);
  }, [activeScreenMobile]);

  const handleOpenBibleSearch = useCallback(
    (event) => {
      event.stopPropagation();
      setActiveScreenMobile("bible_search");
      setOpenBibleSearch(true);

      setSearchParams((prev) => {
        const newParams = new URLSearchParams(prev);
        newParams.delete("2bibleId");
        return newParams;
      });
    },
    [setSearchParams]
  );

  const handleShowSettingsBlock = useCallback(
    (event) => {
      event.stopPropagation();

      setShowSettingsBlock((prevState) => !prevState);
      setShowFontSettings(false);
    },
    [setShowFontSettings]
  );

  const onClickFontSettingsButton = useCallback((event) => {
    event.stopPropagation();
    setShowFontSettings(true);
    setShowSettingsBlock(false);
  }, []);

  const handleShowBibleHistory = useCallback((event) => {
    event.stopPropagation();
    setShowBibleHistory(true);
  }, []);

  const handleShowStrongsCodes = useCallback((event) => {
    event.stopPropagation();
    setShowStrongsCode((prevState) => !prevState);
    setShowSettingsBlock(false);
  }, []);

  const handleHistoryItemClick = (item) => {
    const url = `/bible/?bibleId=${item.b}&bookId=${item.bk}&chapterId=${item.ch}`;
    navigate(url);
    setShowSettingsBlock(false);
  };

  return (
    <RightButtonsContainer>
      {isFirstTranslation && (
        <>
          <BibleSearchButton onClick={handleOpenBibleSearch}>
            <SearchIcon />
          </BibleSearchButton>

          <BibleFontSettingsButton
            ref={settingsButtonRef}
            onClick={handleShowSettingsBlock}
          >
            <ThreeDotsIconStyled color={"#383838"} />
          </BibleFontSettingsButton>

          {showSettingsBlock && (
            <SettingsBlockWrapper ref={settingsRef}>
              <SettingsBlockButtonsContainer>
                {hasStrongsBible && (
                  <SettingsBlockButton onClick={handleShowStrongsCodes}>
                    {t("bible-page.strong-btn")}{" "}
                    {!!showStrongsCode && <CheckIconStyled />}
                  </SettingsBlockButton>
                )}
                <SettingsBlockButton onClick={onClickFontSettingsButton}>
                  {t("bible-page.text-settings-title")}{" "}
                  <FontSettingsIconStyled />
                </SettingsBlockButton>

                {!!bibleHistory.length && (
                  <>
                    {!showBibleHistory ? (
                      <SettingsBlockButton
                        ref={historyButtonRef}
                        onClick={handleShowBibleHistory}
                      >
                        {t("bible-page.history-btn")}{" "}
                        <BibleHistoryIconStyled color={"#383838"} />
                      </SettingsBlockButton>
                    ) : (
                      <BibleHistoryList ref={historyRef}>
                        {bibleHistory.map((historyItem, index) => (
                          <BibleHistoryItem
                            key={
                              index +
                              historyItem.b +
                              historyItem.bk +
                              historyItem.ch
                            }
                            historyItem={historyItem}
                            bibleTranslations={bibleTranslations}
                            handleHistoryItemClick={handleHistoryItemClick}
                          />
                        ))}
                      </BibleHistoryList>
                    )}
                  </>
                )}
              </SettingsBlockButtonsContainer>
            </SettingsBlockWrapper>
          )}

          {showFontSettings && (
            <SettingsBlockWrapper ref={settingsFontRef}>
              <FontSettingsBlock
                bibleFontSettings={bibleFontSettings}
                setBibleFontSettings={setBibleFontSettings}
              />
            </SettingsBlockWrapper>
          )}
        </>
      )}

      {!isFirstTranslation && hasStrongsBible && (
        <StrongButton onClick={handleShowStrongsCodes}>
          {t("bible-page.strong-btn")}{" "}
          {!!showStrongsCode && <CheckIconStyled />}
        </StrongButton>
      )}
    </RightButtonsContainer>
  );
};

export default memo(BibleMainRightButtons);
